import React, { useState } from 'react'
import { Settings, Eye, EyeOff } from 'lucide-react'
import { useFeatures } from '../../hooks/useFeatures'
import { FEATURE_PRESETS, enableFeature, disableFeature, resetFeatures } from '../../config/features'

export const FeatureTogglePanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const features = useFeatures()

  if (!import.meta.env.DEV) return null

  const handlePresetChange = (preset: keyof typeof FEATURE_PRESETS) => {
    const config = FEATURE_PRESETS[preset]
    localStorage.setItem('feature-overrides', JSON.stringify(config))
    window.location.reload()
  }

  const toggleFeature = (feature: keyof typeof features) => {
    if (features[feature]) {
      disableFeature(feature)
    } else {
      enableFeature(feature)
    }
  }

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-12 right-4 z-50 p-2 bg-purple-500 text-white rounded shadow-lg hover:bg-purple-600"
        title="Feature Toggles (Dev Only)"
      >
        <Settings className="w-4 h-4" />
      </button>

      {/* Panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-4 w-80 bg-white rounded shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto scrollbar-thin">
          <div className="p-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Feature Toggles</h3>
              <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">DEV</span>
            </div>
          </div>
          
          <div className="p-3 space-y-3">
            {/* Presets */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Quick Presets</label>
              <div className="flex gap-1">
                {Object.keys(FEATURE_PRESETS).map((preset) => (
                  <button
                    key={preset}
                    onClick={() => handlePresetChange(preset as keyof typeof FEATURE_PRESETS)}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded capitalize"
                  >
                    {preset}
                  </button>
                ))}
                <button
                  onClick={resetFeatures}
                  className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Individual Features */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Individual Features</label>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {Object.entries(features).map(([feature, enabled]) => (
                  <div key={feature} className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 capitalize">
                      {feature.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <button
                      onClick={() => toggleFeature(feature as keyof typeof features)}
                      className={`p-1 rounded ${
                        enabled 
                          ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Current Config */}
            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
              <div className="font-medium mb-1">Console Commands:</div>
              <div>features.enable("cases")</div>
              <div>features.disable("sidebar")</div>
              <div>features.current()</div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default FeatureTogglePanel
